/**
 * Navigator Tuning · Plan history (feature 130). Every Care Navigator plan (FP, Seeker,
 * Guest), filterable, with Best Match + config version tag and no score column (scores
 * of different configs are not comparable). Non-tunable plans are read-only
 * (pre-tuning, or incomplete).
 * Layout: design/Main.dc.html.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfigVersions, usePlanList } from '../hooks/useConfigVersions';
import type { PlanListFilters, PlanListItem } from '../types/navigator-tuning.type';
import { PlanHistoryFilters, USER_TYPES } from './PlanHistoryFilters';

/** "Affected plans" mode: per-plan flags from the impact response, by session id. */
export type AffectedPlans = Record<
  string,
  { bestMatchChanged: boolean; topMatchChangedCount: number }
>;

const USER_TYPE_LABEL = Object.fromEntries(USER_TYPES.map((t) => [t.value, t.label]));

const PAGE_SIZE = 25;
const PLAN_HISTORY_PATH = '/admin/navigator-tuning/plan-history';
/** Open-action column pinned to the right edge while the table scrolls sideways. */
const STICKY_RIGHT = 'sticky right-0 z-10';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatCreated = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const versionTag = (plan: PlanListItem) =>
  plan.configVersion
    ? plan.configVersion.versionNumber
      ? `v${plan.configVersion.versionNumber}`
      : 'Draft'
    : 'Legacy';

const pagerButtonClass =
  'inline-flex min-h-10 items-center rounded-lg border border-[#d9d3c7] bg-white px-3.5 font-semibold text-[#1b2232] hover:bg-[#f4f0e9] disabled:cursor-not-allowed disabled:opacity-50';
const pillClass = 'rounded-full px-2 py-[3px] text-xs font-semibold';

const statusTone = (status: string) => {
  if (status === 'PLAN_GENERATED' || status === 'COMPLETED') return 'bg-[#e3efec] text-[#014d41]';
  if (status === 'NO_RESULTS' || status === 'ABANDONED') return 'bg-[#fdecdc] text-[#9a3412]';
  return 'bg-[#eceef3] text-[#3d4659]';
};

export function PlanHistoryScreen({ affected }: { affected?: AffectedPlans }) {
  const [filters, setFilters] = useState<PlanListFilters>({});
  const [page, setPage] = useState(1);
  const query: PlanListFilters = {
    ...filters,
    page,
    pageSize: PAGE_SIZE,
    ...(affected && { sessionIds: Object.keys(affected).join(',') }),
  };
  const { data, isLoading, isError } = usePlanList(query);
  const { data: versions = [] } = useConfigVersions();

  const update = (next: PlanListFilters) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <section aria-labelledby="plan-history-heading" className="space-y-5">
      <div className="flex flex-col gap-1.5">
        <h2
          id="plan-history-heading"
          className="font-recoleta text-[30px] font-semibold text-[#1b2232]"
        >
          {affected ? 'Affected plans' : 'Care Navigator plans'}
        </h2>
        <p className="text-sm text-[#56607a]">
          Every plan generated by the Navigator, registered and guest. Open a complete plan to
          replay it and tune parameters in a sandbox.
        </p>
      </div>

      <PlanHistoryFilters
        filters={filters}
        onChange={update}
        careStages={data?.careStages}
        versions={versions}
      />

      {isLoading && <p className="text-[#56607a]">Loading plans…</p>}
      {isError && <p role="alert">Failed to load plans.</p>}

      {data && (
        // Bounded height so the horizontal scrollbar stays on screen; header and the
        // open action stay pinned while the table scrolls.
        <div className="max-h-[70vh] overflow-auto rounded-xl border border-border-subtle bg-white">
          <table className="w-full min-w-[980px] text-left text-sm text-[#1b2232]">
            <thead className="sticky top-0 z-20 bg-[#f4f0e9] text-xs font-semibold uppercase tracking-[0.04em] text-[#56607a]">
              <tr>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Care stage</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Specialties</th>
                <th className="px-4 py-3 font-semibold">Best Match</th>
                {affected && <th className="px-4 py-3 font-semibold">Best Match changed</th>}
                {affected && <th className="px-4 py-3 font-semibold">Top Match changed</th>}
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Config</th>
                <th className={`${STICKY_RIGHT} bg-[#f4f0e9] px-4 py-3`}>
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const change = affected?.[p.sessionId];
                return (
                  <tr key={p.sessionId} className="border-t border-[#efeae1]">
                    <td className="whitespace-nowrap px-4 py-3.5 text-[#56607a]">
                      {formatCreated(p.createdAt)}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-[13px]">{p.sessionId.slice(0, 8)}</td>
                    <td className="px-4 py-3.5">
                      {p.userType === 'guest' ? (
                        <span className="rounded-full bg-[#f1ede6] px-2 py-0.5 text-xs font-semibold text-[#56607a]">
                          Guest
                        </span>
                      ) : (
                        <>
                          <span className="font-bold">{USER_TYPE_LABEL[p.userType]}</span>
                          {p.ownerEmail && (
                            <span className="block text-[#56607a]">{p.ownerEmail}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">{p.careStage?.name ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[#56607a]">
                      {[p.state, p.county].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">
                      {p.specialtiesCount}
                      {p.emptySpecialtiesCount > 0 && (
                        <span className="ml-1.5 text-xs font-semibold text-[#9a3412]">
                          {p.emptySpecialtiesCount} empty
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">{p.bestMatch?.name ?? '—'}</td>
                    {affected && (
                      <td className="px-4 py-3.5">{change?.bestMatchChanged ? 'Yes' : 'No'}</td>
                    )}
                    {affected && (
                      <td className="px-4 py-3.5">
                        {change?.topMatchChangedCount
                          ? `${change.topMatchChangedCount} ${change.topMatchChangedCount === 1 ? 'specialty' : 'specialties'}`
                          : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3.5">
                      <span className={`${pillClass} ${statusTone(p.status)}`}>{p.status}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[#56607a]">
                      <span>{versionTag(p)}</span>
                      {!p.tunable && (
                        <span
                          title={
                            p.preTuning
                              ? 'Generated before tuning data was captured: read-only'
                              : 'Tuning data incomplete (no config version, pending search or missing candidates): read-only'
                          }
                          className="ml-2 rounded-full bg-[#eceef3] px-2 py-0.5 text-xs font-semibold text-[#3d4659]"
                        >
                          {p.preTuning ? 'Pre-tuning' : 'Incomplete · read only'}
                        </span>
                      )}
                    </td>
                    <td className={`${STICKY_RIGHT} whitespace-nowrap px-4 py-2 text-center`}>
                      <Link
                        to={`${PLAN_HISTORY_PATH}/${p.sessionId}`}
                        aria-label={`Open plan ${p.sessionId.slice(0, 8)}`}
                        className="inline-flex min-h-10 items-center whitespace-nowrap rounded-lg border border-bquest-green bg-white px-3.5 font-semibold text-bquest-green shadow-sm hover:bg-[#e3efec]"
                      >
                        {p.tunable ? 'Open & tune' : 'View'}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {items.length === 0 && (
            <p className="p-6 text-center text-sm text-[#56607a]">
              {filters.tunable && data.cutoffAt
                ? `No tunable plans yet. Complete plans appear from ${formatDate(data.cutoffAt)}.`
                : 'No plans match these filters.'}
            </p>
          )}
        </div>
      )}

      {data && (
        <div className="flex items-center justify-between pb-20 text-sm text-[#56607a]">
          <span>
            Showing {items.length} of {data.total} plans
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={pagerButtonClass}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className={pagerButtonClass}
              disabled={page >= lastPage}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
